import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuthStore, useUIStore, useChatStore } from "../context/store.js";
import { VylappLogo, Avatar, Ic, ICONS } from "./UI.jsx";

const B = {
  bg2: "#0D0C18", bg3: "#131124",
  violet: "#7C3AED", violetLt: "#8B5CF6",
  violetDim: "rgba(124,58,237,0.15)", violetBorder: "rgba(124,58,237,0.3)",
  vibeGreen: "#10F5A0", coral: "#FF6B6B", sky: "#38BDF8",
  text: "#F0EFFE", text2: "#7C7A9E", text3: "#3D3B5C",
  border2: "rgba(124,58,237,0.08)",
  grad: "linear-gradient(135deg, #7C3AED, #10F5A0)",
  font: "'Sora', system-ui, sans-serif",
  mono: "'Space Mono', monospace",
};

function NavItem({ icon, label, active, badge, expanded, onClick }) {
  return (
    <button onClick={onClick} style={{
      display: "flex", alignItems: "center",
      gap: expanded ? 14 : 0, justifyContent: expanded ? "flex-start" : "center",
      padding: expanded ? "11px 14px" : "11px",
      borderRadius: 12, border: "none", cursor: "pointer", width: "100%",
      background: active ? B.violetDim : "none",
      color: active ? B.violetLt : B.text2,
      fontFamily: B.font, fontWeight: active ? 700 : 400,
      transition: "all 0.15s", position: "relative",
    }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = "rgba(124,58,237,0.08)"; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = "none"; }}>
      {active && <div style={{ position:"absolute",left:0,top:"20%",bottom:"20%",width:3,borderRadius:"0 3px 3px 0",background:B.grad }} />}
      <div style={{ position: "relative", flexShrink: 0 }}>
        <Ic d={icon} s={22} c={active ? B.violetLt : B.text2} />
        {badge > 0 && (
          <span style={{
            position: "absolute", top: -4, right: -6, minWidth: 18, height: 18,
            borderRadius: 9, background: B.grad, color: "#fff", fontSize: 10,
            fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center",
            padding: "0 4px", border: `2px solid ${B.bg2}`,
          }}>{badge}</span>
        )}
      </div>
      {expanded && <span style={{ fontSize: 17, whiteSpace: "nowrap" }}>{label}</span>}
    </button>
  );
}

export default function Sidebar({ onNav, onCall, onStream }) {
  const { user, logout }   = useAuthStore();
  const { unreadChats, unreadAlerts } = useUIStore();
  const location           = useLocation();
  const [expanded, setExpanded] = useState(false);

  const currentTab = location.pathname.slice(1) || "feed";

  const navItems = [
    { key: "feed",    icon: ICONS.feed,    label: "Vibes"         },
    { key: "explore", icon: ICONS.explore, label: "Explore"       },
    { key: "spaces",  icon: ICONS.spaces,  label: "Spaces"        },
    { key: "chat",    icon: ICONS.chat,    label: "Connect",   badge: unreadChats },
    { key: "alerts",  icon: ICONS.bell,    label: "Alerts",    badge: unreadAlerts },
    { key: "profile", icon: ICONS.user,    label: "Profile"       },
  ];

  return (
    <div style={{
      width: expanded ? 250 : 76,
      borderRight: `1px solid ${B.border2}`,
      display: "flex", flexDirection: "column", padding: "8px 0",
      background: B.bg2, flexShrink: 0,
      transition: "width 0.25s cubic-bezier(0.4,0,0.2,1)",
      overflow: "hidden",
    }}>
      {/* Logo / toggle */}
      <button onClick={() => setExpanded(v => !v)} style={{
        display: "flex", alignItems: "center",
        gap: expanded ? 12 : 0, justifyContent: expanded ? "flex-start" : "center",
        padding: expanded ? "10px 16px" : "10px",
        margin: "4px 8px", borderRadius: 14, border: "none", background: "none", cursor: "pointer",
      }}
        onMouseEnter={e => e.currentTarget.style.background = B.violetDim}
        onMouseLeave={e => e.currentTarget.style.background = "none"}>
        <VylappLogo size={36} showText={expanded} textSize={17} />
      </button>

      {/* Nav */}
      <nav style={{ flex: 1, padding: "8px", display: "flex", flexDirection: "column", gap: 2, marginTop: 8 }}>
        {navItems.map(item => (
          <NavItem key={item.key}
            icon={item.icon} label={item.label}
            active={currentTab === item.key} badge={item.badge}
            expanded={expanded} onClick={() => onNav(item.key)} />
        ))}
      </nav>

      {/* Quick Connect */}
      <div style={{ padding: "8px", borderTop: `1px solid ${B.border2}` }}>
        {expanded && <div style={{ color: B.text3, fontSize: 9, letterSpacing: 1.5, padding: "0 10px 6px", fontFamily: B.mono, textTransform: "uppercase" }}>Quick Connect</div>}
        {[
          { label: "Voice",   icon: ICONS.phone,  color: B.vibeGreen, action: () => onCall({ type:"voice", user: { displayName:"Friend", initials:"FR", color:B.vibeGreen } }) },
          { label: "Video",   icon: ICONS.video,  color: B.sky,       action: () => onCall({ type:"video", user: { displayName:"Friend", initials:"FR", color:B.sky } }) },
          { label: "Go Live", icon: ICONS.stream, color: B.coral,     action: onStream },
        ].map((btn, i) => (
          <button key={i} onClick={btn.action} style={{
            display: "flex", alignItems: "center",
            gap: expanded ? 10 : 0, justifyContent: expanded ? "flex-start" : "center",
            padding: expanded ? "8px 12px" : "10px",
            width: "100%", borderRadius: 10, border: "none",
            background: "none", color: btn.color, fontFamily: B.font, fontSize: 13,
            cursor: "pointer", transition: "all 0.15s", whiteSpace: "nowrap",
          }}
            onMouseEnter={e => e.currentTarget.style.background = `${btn.color}12`}
            onMouseLeave={e => e.currentTarget.style.background = "none"}>
            <Ic d={btn.icon} s={expanded ? 16 : 20} c={btn.color} />
            {expanded && btn.label}
          </button>
        ))}
      </div>

      {/* Vibe / post button */}
      <div style={{ padding: "8px" }}>
        {expanded ? (
          <button onClick={() => onNav("feed")} style={{
            width: "100%", padding: "12px", borderRadius: 9999, border: "none",
            background: B.grad, color: "#fff", fontFamily: B.font, fontWeight: 700,
            fontSize: 16, cursor: "pointer", boxShadow: "0 4px 20px rgba(124,58,237,0.4)",
          }}>✦ Vibe</button>
        ) : (
          <button onClick={() => onNav("feed")} style={{
            width: 44, height: 44, margin: "0 auto", display: "flex",
            alignItems: "center", justifyContent: "center",
            borderRadius: "50%", border: "none", background: B.grad,
            color: "#fff", fontWeight: 900, fontSize: 20, cursor: "pointer",
            boxShadow: "0 4px 20px rgba(124,58,237,0.4)",
          }}>✦</button>
        )}
      </div>

      {/* Me */}
      <div onClick={() => onNav("profile")} style={{
        display: "flex", alignItems: "center",
        gap: expanded ? 10 : 0, justifyContent: expanded ? "flex-start" : "center",
        padding: expanded ? "12px 16px" : "12px 8px",
        cursor: "pointer", borderTop: `1px solid ${B.border2}`,
        transition: "background 0.15s",
      }}
        onMouseEnter={e => e.currentTarget.style.background = B.violetDim}
        onMouseLeave={e => e.currentTarget.style.background = "none"}>
        {user && <Avatar user={user} size={34} showOnline />}
        {expanded && user && (
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: B.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.displayName}</div>
            <div style={{ color: B.text2, fontSize: 12, fontFamily: B.mono }}>{user.handle}</div>
          </div>
        )}
      </div>

      {/* Logout Row */}
      <div style={{ padding: "4px 8px" }}>
        <button onClick={logout} style={{
          display: "flex", alignItems: "center",
          gap: expanded ? 14 : 0, justifyContent: expanded ? "flex-start" : "center",
          padding: expanded ? "10px 14px" : "10px",
          borderRadius: 12, border: "none", cursor: "pointer", width: "100%",
          background: "rgba(255,107,107,0.06)",
          color: B.coral,
          fontFamily: B.font, fontWeight: 600,
          transition: "all 0.15s",
        }}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,107,107,0.15)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,107,107,0.06)"; }}
          title="Logout">
          <Ic d={ICONS.logout} s={22} c={B.coral} />
          {expanded && <span style={{ fontSize: 16 }}>Logout</span>}
        </button>
      </div>
    </div>
  );
}
